/**
 * Snakes and Ladders Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 *
 * Follows BattleshipStatisticsService pattern.
 */

import {
  SnakesLaddersStatistics,
  SnakesLaddersGameResult,
  SnakesLaddersGameStats,
  SnakesLaddersDifficulty,
  SnakesLaddersGameSettings,
  PlayerColor,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class SnakesLaddersStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'snakes_ladders_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): SnakesLaddersStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): SnakesLaddersGameStats {
    return {
      wins: 0,
      losses: 0,
      winPercentage: 0,
      averageMoves: 0,
      snakesHit: 0,
      laddersClimbed: 0,
    };
  }

  /** Get statistics for a user (or global if no userId). */
  getStatistics(userId?: string): SnakesLaddersStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as SnakesLaddersStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get snakes & ladders statistics:', error);
      return this.createEmptyStats();
    }
  }

  /** Record a completed game result. */
  recordGameResult(
    result: SnakesLaddersGameResult,
    playerColor: PlayerColor,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerColor);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerColor);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerColor);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record snakes & ladders game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: SnakesLaddersGameStats,
    result: SnakesLaddersGameResult,
    playerColor: PlayerColor
  ): void {
    const totalGames = stats.wins + stats.losses;

    if (result.winner === playerColor) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    const newTotalGames = stats.wins + stats.losses;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    // Update average moves
    stats.averageMoves =
      newTotalGames > 0
        ? (stats.averageMoves * totalGames + result.totalMoves) / newTotalGames
        : result.totalMoves;

    // Update snakes/ladders totals
    stats.snakesHit += result.snakesHit;
    stats.laddersClimbed += result.laddersClimbed;
  }

  /** Get statistics for a specific mode and difficulty. */
  getModeStatistics(
    mode: SnakesLaddersGameSettings['mode'],
    difficulty?: SnakesLaddersDifficulty,
    userId?: string
  ): SnakesLaddersGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  /** Reset all statistics. */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset snakes & ladders statistics:', error);
      return false;
    }
  }
}
